/**
 * Funnel events for the paywall.
 *
 * Every event carries the same five dimensions: which direction the person was
 * routed to, which pattern the screens argued from, what we offered, what the
 * exit ladder offered, and which variant they saw. Without those, "save rate"
 * and "revenue per completed quiz" are not computable — you can count clicks
 * but you cannot say which offer earned them.
 *
 * `checkout_initiated` also goes through `trackEvent`, which persists it for
 * referral attribution; the rest are Amplitude/GTM only, so the first-party
 * events table stays the small milestone log it was designed to be.
 */
import sendAnalyticsEvent from '../utils/sendAnalyticsEvent';
import trackEvent from '../utils/trackEvent';
import { PromoState } from '../config/paywall';
import { PaywallProfile } from './data';

export type PaywallEvent =
    | 'paywall_viewed'
    | 'promo_granted'
    | 'promo_expired'
    | 'exit_intent_triggered'
    | 'exit_modal_viewed'
    | 'exit_modal_continue_clicked'
    | 'exit_modal_leave_clicked'
    | 'downsell_viewed'
    | 'downsell_accepted'
    | 'downsell_rejected'
    | 'checkout_initiated'
    | 'checkout_cancelled'
    | 'checkout_recovery_viewed'
    | 'checkout_recovery_accepted'
    | 'trial_started';

export interface PaywallContext {
    quizId?: string;
    profile: PaywallProfile;
    promo: PromoState | null;
    /** Which build of the funnel this person saw, for A/B comparison. */
    variant: string;
    email?: string;
}

/** The five dimensions, derived once so no caller assembles them by hand. */
export function dimensions(ctx: PaywallContext): Record<string, any> {
    return {
        primaryTrack: ctx.profile.dirCode || null,
        topPattern: ctx.profile.blocker?.code || null,
        originalOffer: 'trial7_month19_99',
        exitOffer: ctx.promo?.valid ? `promo_${ctx.promo.code}_${ctx.promo.percent}` : null,
        funnelVariant: ctx.variant,
        index: ctx.profile.index,
    };
}

export function trackPaywall(event: PaywallEvent, ctx: PaywallContext, extra?: Record<string, any>): void {
    const data = { quiz_id: ctx.quizId, ...dimensions(ctx), ...(extra || {}) };

    sendAnalyticsEvent({ eventName: event, data });

    // The one event the referral system also needs on our own side.
    if (event === 'checkout_initiated') {
        trackEvent('checkout_initiated', {
            quizId: ctx.quizId,
            email: ctx.email,
            metadata: { ...dimensions(ctx), source: 'paywall' },
        });
    }
}
